import React, {ReactElement, createRef, RefObject} from 'react';
import {FilterMatchMode} from 'primereact/api';
import {Column} from 'primereact/column';
import MComponent from '../../Toolkit/Components/Component';
import WithRouterProps from '../../Toolkit/WithRouterProps';
import MPage, {MPageProps} from '../../Toolkit/Components/Shell/Page';
import MTable, {MTableProps} from '../../Components/Toolkit/Table';
import MToolbar from '../../Toolkit/Components/Controls/Toolbar';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import MButton from '../../Toolkit/Components/Controls/Button';
import {MNewIconName, MWarningIcon} from '../../Toolkit/Icons';
import withRouter from '../../Toolkit/WithRouter';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import NSwagAdapter from '../../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../../Toolkit/Api/NSwagClientConfiguration';
import TrafficManagementAccessTokenProvider from '../../Api/TrafficManagementAccessTokenProvider';
import MBackButton from '../../Toolkit/Components/Controls/BackButton';
import EnumContext from '../../Context/enum-context';
import {CreateProjectRequest, ProjectClient} from '../../Api/ProjectClient';
import {Project, ProjectCreateReply, ProjectMultiReply} from '../../Api/ControllerTypes/ProjectTypes';
import AddProjectDialog, {ProjectData} from '../../Dialogs/AddProjectDialog';
import AlertDialog from '../../Components/Toolkit/AlertDialog';

type ProjectRow = {
    data: Project;
}

type RouteParams = {
    startDate: number;
};

type Props = WithMsalProps & WithRouterProps<RouteParams> & {};

/** */
type State = {
    page: MPageProps;
    table: MTableProps<ProjectRow>;
    selectedRow?: ProjectRow | ProjectRow[];
}

class ListProjectsPage extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private readonly m_projectClient: NSwagAdapter<ProjectClient>;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_dialogRef: RefObject<AddProjectDialog>;
    private readonly m_alertRef: RefObject<AlertDialog>;

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_projectClient = new NSwagAdapter<ProjectClient>(new ProjectClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_pageRef = createRef<MPage>();
        this.m_dialogRef = createRef<AddProjectDialog>();
        this.m_alertRef = createRef<AlertDialog>();

        this.initializeState();

    }

    private bindMethods(): void {

        this.isRowSelected = this.isRowSelected.bind(this);
        this.new_Click = this.new_Click.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.new_Click = this.new_Click.bind(this);
        this.OnAddProject = this.OnAddProject.bind(this);
    }

    /** */
    private initializeState(): void {

        //  Page
        const page: MPageProps = {
            locked: false
        };

        //  Table
        const table: MTableProps<ProjectRow> = {
            rowData: [],
            filters: {
                'data.code': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.name': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                }
            }
        };

        this.state = {
            page: page,
            table: table,
            selectedRow: undefined
        };

    }

    public componentDidMount(): void {

    }

    private isRowSelected(): boolean {
        return this.state.selectedRow === undefined;
    }

    /** */
    private async new_Click(): Promise<void> {
        this.m_dialogRef.current!.show(this.OnAddProject)
    }

    private async OnAddProject(data: ProjectData): Promise<void> {
        const reply = await this.m_projectClient.callWithReply(
            ProjectCreateReply, (client) => client.create(new CreateProjectRequest({
                    code: data.code,
                    name: data.name
                })));

        reply.throwIfFailed();

        if(reply.reply!.exists){
            this.m_alertRef.current!.show("Project Exists",
                "The requested Project Code already exists. Please enter a unique Project code.",
                MWarningIcon,
                <MButton disabled={false}
                         label={"OK"}
                         onClick={() => {this.m_alertRef.current!.hide()}}
                />)
            return;
        }

        this.m_pageRef.current!.messageBar.info("Project added successfully.");
        this.page_onLoadData();
    }

    private table_onSelectionChange(selection: ProjectRow | ProjectRow[] | undefined): void {
        this.safeSetState(draft => draft.selectedRow = selection);
    }

    private async page_onLoadData(): Promise<void> {
        //Get Row Data
        const listResult = await this.m_projectClient.callWithReply(
            ProjectMultiReply, (client) => client.getAll());
        listResult.throwIfFailed();

        this.safeSetState((draft: State) => {

            draft.table.rowData = listResult.reply!.projects.map((w: Project) => {
                return {data: w} as ProjectRow;
            });

        });


    }

    private getToolbarTemplate(): ReactElement<MToolbar> {
        return (
            <MToolbar>
                <MBackButton disabled={false}/>
                <MSpacer/>
                <MButton label="Add"
                         color="success"
                         icon={MNewIconName}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.new_Click}/>
            </MToolbar>
        );

    }

    private dateTemplate = (rowData: ProjectRow) : ReactElement => {
        try {
            let createdDate = new Date(rowData.data.createdOn!);

            return <div>
                {createdDate.toLocaleDateString()}
            </div>
        } catch {
            return <div>{''}</div>;
        }
    }

    public render(): ReactElement {
        return (
            <MPage title={'Project List'}
                   locked={this.state.page.locked}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <AddProjectDialog
                    ref={this.m_dialogRef}
                />

                <AlertDialog
                    ref={this.m_alertRef}
                />

                <MTable
                    rowKey="data.id"
                    rowData={this.state.table.rowData}
                    filters={this.state.table.filters}
                    multipleSelection={false}
                    onSelectionChange={this.table_onSelectionChange}
                    sortMode={'multiple'}
                    multiSortMeta={[{field: 'data.id', order: 1}]}>

                    <Column field="data.code"
                            header="Project Code"
                            filter={true}
                            sortable={true}
                            style={{width: '20%'}}/>
                    <Column field="data.name"
                            header="Project Name"
                            filter={true}
                            sortable={true}
                            style={{width: '30%'}}/>
                    <Column field="data.createdBy"
                            header="Created By"
                            filter={false}
                            sortable={true}
                            style={{width: '30%'}}/>
                    <Column field="data.createdOn"
                            header="Created On"
                            dataType={"date"}
                            filter={false}
                            sortable={true}
                            body={this.dateTemplate}
                            style={{width: '20%'}}/>
                </MTable>
            </MPage>
        );
    }
}

export default withMsal(withRouter(ListProjectsPage));
